import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import AxiosInstance from "../../AxiosInstance";
import moment from "moment";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import "./style.css";
import { useLocation, useNavigate } from "react-router-dom";
import { LoaderComponent } from "../../../components/Icon/Index";
import showToast from "../../../components/Toast/Toster";
import BlueButton from "../../../components/Button/BlueButton";
import WhiteButton from "../../../components/Button/WhiteButton";
import {
  CheckCircle2Icon,
  PersonStandingIcon,
  PlaneLandingIcon,
} from "lucide-react";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";  
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ZoomIn from "@mui/icons-material/ZoomIn";
import ZoomOut from "@mui/icons-material/ZoomOut";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { handleAuth } from "../../../components/Login/Auth";
import { DisplayImage } from "../../../components/Files/DisplayFiles";
import { readFirstSheetAoA } from "../../../utils/xlsxHelpers";
function ContractsDetails() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [DateDecode, setDateDecode] = useState("MM/DD/YYYY");
  const baseUrl = import.meta.env.VITE_BASE_API;
  const navigate = useNavigate();
  const location = useLocation();
  const [contractDetail, setContractDetail] = useState({});
  const [loader, setLoader] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressIntervalRef = useRef(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [imageZoom, setImageZoom] = useState(100);
  const [modalWidth, setModalWidth] = useState("sm");
  const [previewContent, setPreviewContent] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(10);
  const [fileType, setFileType] = useState(null);
  const imgRef = useRef(null);
  
  // Use timezone-aware date formatter hook
  const formatDateWithUserTimezone = useUserTimezoneFormatter();
  
  // Helper function to format address
  const formatAddress = (location) => {
    if (!location) return "Address not available";
    
    const address = location?.Address || "";
    const city = location?.City || "";
    const state = location?.State || "";
    const zip = location?.Zip || "";
    
    // Check if all address fields are empty
    if (!address && !city && !state && !zip) {
      return "Address not available";
    }
    
    // Build address string with only available fields
    const addressParts = [];
    if (address) addressParts.push(address);
    if (city) addressParts.push(city);
    if (state) addressParts.push(state);
    if (zip) addressParts.push(zip);
    
    return addressParts.join(", ");
  };
  
  const fetchDateData = async () => {
    try {
      const res = await handleAuth(navigate, location);
      setDateDecode(res?.themes?.Format || "MM/DD/YYYY");
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchData = async () => {
    try {
      const res = await AxiosInstance.get(
        `/v1/contract/contract_details/${location?.state?.id}`
      );
      setContractDetail(res?.data?.data);
    } catch (error) {
      console.error("Error fetching contract details: ", error?.message);
      showToast.error("Failed to fetch contract details");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchDateData();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateImageSize = () => {
    if (imgRef.current && previewImage && previewDialogOpen) {
      const img = imgRef.current;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      
      if (naturalWidth > 0 && naturalHeight > 0) {
        const containerWidth = img.parentElement?.clientWidth || 560;
        const padding = 40;
        const availableWidth = containerWidth - padding;
        
        // Calculate image width based on zoom
        let imageWidth;
        if (imageZoom === 100) {
          imageWidth = Math.min(naturalWidth, availableWidth);
          img.style.width = "auto";
          img.style.maxWidth = "100%";
          img.style.height = "auto";
          img.style.maxHeight = "none";
        } else {
          const displayWidthAt100 = Math.min(naturalWidth, availableWidth);
          imageWidth = (displayWidthAt100 * imageZoom) / 100;
          img.style.width = `${imageWidth}px`;
          img.style.height = "auto";
          img.style.maxWidth = "none";
          img.style.maxHeight = "none";
        }
        
        // Adjust modal width based on image size and zoom
        const totalWidth = imageWidth + padding + 80; // image + padding + dialog padding
        if (totalWidth < 500) {
          setModalWidth("xs");
        } else if (totalWidth < 700) {
          setModalWidth("sm");
        } else if (totalWidth < 900) {
          setModalWidth("md");
        } else {
          setModalWidth("lg");
        }
      }
    }
  };

  useEffect(() => {
    updateImageSize();
  }, [imageZoom, previewImage, previewDialogOpen]);

  // CSV parsing function
  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
    return lines.map((line) => {
      const result = [];
      let current = "";
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];
        
        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === "," && !inQuotes) {
          result.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    });
  };

  // Check if file is CSV or Excel
  const isCSVFile = (fileName) => {
    if (typeof fileName === "string") {
      const name = fileName.split("?")[0].toLowerCase();
      return name.endsWith(".csv") || name.endsWith(".csvx");
    }
    return false;
  };

  const isExcelFile = (fileName) => {
    if (typeof fileName === "string") {
      const name = fileName.split("?")[0].toLowerCase();
      return name.endsWith(".xls") || name.endsWith(".xlsx") || name.endsWith(".excel");
    }
    return false;
  };

  const isPdfFile = (fileName) => {
    if (typeof fileName === "string") {
      const name = fileName.split("?")[0].toLowerCase();
      return name.endsWith(".pdf");
    }
    return false;
  };

  const isTxtFile = (fileName) => {
    if (typeof fileName === "string") {
      const name = fileName.split("?")[0].toLowerCase();
      return name.endsWith(".txt");
    }
    return false;
  };

  const handleImageClick = async (imageUrl) => {
    setPreviewDialogOpen(true);
    setImageZoom(100);
    setModalWidth("sm");
    setPreviewContent(null);
    setPreviewPage(0);
    setPreviewRowsPerPage(10);
    setIsLoadingPreview(false);

    // Extract filename from URL
    const fileName = imageUrl.split("/").pop() || "";

    // Detect file type
    if (isPdfFile(fileName)) {
      setFileType("pdf");
      setModalWidth("md");
      // Fetch the PDF and use a same-origin `blob:` URL for the iframe —
      // the cross-origin file URL was loading as a sandboxed PDF that
      // could surface as a broken file icon under Chrome's Site Isolation.
      // The blob URL is revoked on dialog close.
      try {
        const response = await fetch(imageUrl);
        if (response.ok) {
          const rawBlob = await response.blob();
          // Force application/pdf so the iframe renders the PDF viewer
          // instead of triggering a download (some servers return
          // application/octet-stream for static files).
          const pdfBlob =
            rawBlob.type === "application/pdf"
              ? rawBlob
              : new Blob([rawBlob], { type: "application/pdf" });
          setPreviewImage(URL.createObjectURL(pdfBlob));
        } else {
          setPreviewImage(imageUrl);
        }
      } catch (err) {
        console.error("PDF preview fetch failed:", err);
        setPreviewImage(imageUrl);
      }
    } else if (isCSVFile(fileName)) {
      setPreviewImage(imageUrl);
      setFileType("csv");
      setModalWidth("lg");
      await parseAndPreviewFile(imageUrl, "csv");
    } else if (isExcelFile(fileName)) {
      setPreviewImage(imageUrl);
      setFileType("excel");
      setModalWidth("lg");
      await parseAndPreviewFile(imageUrl, "excel");
    } else if (isTxtFile(fileName)) {
      setPreviewImage(imageUrl);
      setFileType("txt");
      setModalWidth("sm");
    } else {
      setPreviewImage(imageUrl);
      setFileType("image");
      setModalWidth("sm");
    }
  };

  const parseAndPreviewFile = async (fileUrl, type) => {
    setIsLoadingPreview(true);
    setPreviewContent(null);
    setPreviewPage(0);
    setPreviewRowsPerPage(10);
    
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }
      const blob = await response.blob();

      if (type === "csv") {
        const text = await blob.text();
        const rows = parseCSV(text);
        if (rows.length > 0) {
          setPreviewContent({ type: "csv", data: rows });
        } else {
          throw new Error("CSV file is empty");
        }
      } else if (type === "excel") {
        const arrayBuffer = await blob.arrayBuffer();
        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          throw new Error("Excel file is empty or invalid");
        }
        const data = await readFirstSheetAoA(arrayBuffer);
        if (!data || data.length === 0) {
          throw new Error("Excel file is empty or has no sheets");
        }
        setPreviewContent({ type: "excel", data: data });
      }
    } catch (error) {
      showToast.error(`Failed to preview file: ${error.message}`);
      setPreviewContent({ type: type, error: error.message, data: null });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    if (previewImage && previewImage.startsWith("blob:")) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewDialogOpen(false);
    setPreviewImage("");
    setImageZoom(100);
    setPreviewContent(null);
    setFileType(null);
    setPreviewPage(0);
    setPreviewRowsPerPage(10);
    setIsLoadingPreview(false);
  };

  const handlePreviewPageChange = (event, newPage) => {
    setPreviewPage(newPage);
  };

  const handlePreviewRowsPerPageChange = (event) => {
    setPreviewRowsPerPage(parseInt(event.target.value, 10));
    setPreviewPage(0);
  };

  const getPreviewData = () => {
    if (!previewContent?.data) return [];
    const dataWithoutHeader = previewContent.data.slice(1);
    const startIndex = previewPage * previewRowsPerPage;
    return dataWithoutHeader.slice(startIndex, startIndex + previewRowsPerPage);
  };

  const getTotalRows = () => {
    if (!previewContent?.data?.length) return 0;
    return Math.max(0, previewContent.data.length - 1);
  };

  const handleZoomIn = () => {
    setImageZoom((prev) => Math.min(prev + 25, 300)); // Max 300%
  };

  const handleZoomOut = () => {
    setImageZoom((prev) => Math.max(prev - 25, 50)); // Min 50%
  };

  const handleResetZoom = () => {
    setImageZoom(100);
  };

  const handleDownload = async () => {
    if (previewImage) {
      try {
        const response = await fetch(previewImage);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = previewImage.split("/").pop() || "attachment";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      } catch (error) {
        console.error("Error downloading file:", error);
        // Fallback: try direct download without opening in new tab
        const link = document.createElement("a");
        link.href = previewImage;
        link.download = previewImage.split("/").pop() || "attachment";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      }
    }
  };
 
  // Helper function to sanitize filename by removing/replacing invalid characters
  const sanitizeFileName = (str) => {
    if (!str) return "";
    return str
      .replace(/[<>:"/\\|?*]/g, "") // Remove invalid filename characters
      .replace(/\s+/g, "_") // Replace spaces with underscores
      .trim();
  };

  // Helper function to generate a readable PDF filename
  const generatePDFFileName = () => {
    const customerFirstName = contractDetail?.customer?.FirstName || "";
    const customerLastName = contractDetail?.customer?.LastName || "";
    const customerName = `${customerFirstName} ${customerLastName}`.trim();
    const contractNumber = contractDetail?.ContractNumber || "";
    const contractTitle = contractDetail?.Title || "";

    // Build filename parts
    const parts = ["Contract"];
    
    if (customerName) {
      parts.push(sanitizeFileName(customerName));
    }
    
    if (contractNumber) {
      parts.push(`#${contractNumber}`);
    }
    
    if (contractTitle && contractTitle !== customerName) {
      parts.push(sanitizeFileName(contractTitle));
    }

    // Fallback to ID if no other info available
    if (parts.length === 1) {
      parts.push(location?.state?.id || "document");
    }

    return `${parts.join("_")}.pdf`;
  };

  const simulateProgress = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    setProgress(0);
    let simulatedProgress = 0;
    progressIntervalRef.current = setInterval(() => {
      simulatedProgress += 2;
      if (simulatedProgress >= 100) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
        setProgress(100);
      } else {
        setProgress(simulatedProgress);
      }
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const [openPdfPreview, setOpenPdfPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);

  // Like General Ledger: one action opens preview; Download is inside the dialog
  const downloadPdf = async () => {
    if (downloadingPdf) return;
    try {
      setDownloadingPdf(true);
      setProgress(0);
      simulateProgress();
      const res = await AxiosInstance.post(
        `/v1/contract/contractpdf/${location?.state?.id}`,
        { dateFormat: DateDecode }
      );
      if (res?.data?.statusCode === 200 && res?.data?.fileName) {
        const url = `${baseUrl}/v1/cdn/upload/${res?.data?.fileName}`;
        const response = await fetch(url);
        if (!response?.ok) throw new Error(`HTTP error! status: ${response?.status}`);
        const blob = await response?.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        setPreviewPdfUrl(blobUrl);
        setOpenPdfPreview(true);
      } else {
        throw new Error("Failed to generate PDF");
      }
    } catch (error) {
      console.error("Error loading PDF:", error);
      showToast.error(error?.message || "Failed to load PDF");
    } finally {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setProgress(0);
      setDownloadingPdf(false);
    }
  };

  const handleClosePdfPreview = () => {
    if (previewPdfUrl && previewPdfUrl.startsWith("blob:")) {
      window?.URL?.revokeObjectURL(previewPdfUrl);
    }
    setPreviewPdfUrl(null);
    setOpenPdfPreview(false);
  };

  const handleDownloadFromPreview = () => {
    if (!previewPdfUrl) return;
    const link = document.createElement("a");
    link.href = previewPdfUrl;
    link.download = generatePDFFileName();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast.success("PDF downloaded successfully!");
  };
 
  const getStatusColor = (status) => {
    if (isDark) {
      switch (status) {
        case "Unscheduled":
          return {
            bg: "rgba(248, 113, 113, 0.22)",
            color: "#fecaca",
            icon: <PersonStandingIcon />,
          };
        case "Today":
          return {
            bg: "rgba(96, 165, 250, 0.22)",
            color: "#bfdbfe",
            icon: <CheckCircle2Icon />,
          };
        case "Upcoming":
          return {
            bg: "rgba(52, 211, 153, 0.22)",
            color: "#a7f3d0",
            icon: <CalendarTodayIcon />,
          };
        case "Scheduled":
          return {
            bg: "rgba(251, 191, 36, 0.2)",
            color: "#fde68a",
            icon: <CalendarTodayIcon />,
          };
        default:
          return {
            bg: "rgba(255,255,255,0.08)",
            color: "rgba(226,232,240,0.92)",
            icon: <PlaneLandingIcon />,
          };
      }
    }
    switch (status) {
      case "Unscheduled":
        return {
          bg: "#ffc6c6",
          color: "#ff0000",
          icon: <PersonStandingIcon />,
        };
      case "Today":
        return { bg: "#dbecff", color: "#3595ff", icon: <CheckCircle2Icon /> };
      case "Upcoming":
        return { bg: "#aef6d3", color: "#089f57", icon: <CalendarTodayIcon /> };
      case "Scheduled":
        return { bg: "#ffe9bc", color: "#ffaf0b", icon: <CalendarTodayIcon /> };
      default:
        return { bg: "#f5f5f5", color: "#757575", icon: <PlaneLandingIcon /> };
    }
  };

  const statusConfig = getStatusColor(contractDetail?.Status);

  return (
    <Box>
      {downloadingPdf ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
          >
            <CircularProgress
              variant="determinate"
              value={progress}
              size={90}
              thickness={2}
              sx={{ color: "#D4943A" }}
            />
            <Typography
              className="text-blue-color"
              variant="caption"
              component="div"
              color="textSecondary"
              style={{
                position: "absolute",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              {`${progress}%`}
            </Typography>
          </Box>
        </Box>
      ) : loader ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "20vh", marginTop: "25%"
          }}
        >
          <LoaderComponent loader={loader} height="50" width="50" />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            px: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              mb: 3,
              p: { xs: 1, sm: 2 },
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              boxShadow: (t) =>
                t.palette.mode === "dark" ? "0 1px 3px rgba(0,0,0,0.35)" : "var(--shadow-md)",
              gap: { xs: 2, sm: 0 },
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  backgroundColor: "var(--bg-blue-color)",
                  color: "white",
                  "&:hover": { backgroundColor: "#052a4f" },
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "var(--text-blue-color)",
                    fontSize: { xs: "1.1rem", sm: "1.5rem" },
                  }}
                >
                  Contract Details
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  }}
                >
                  #{contractDetail?.ContractNumber || "N/A"} •{" "}
                  {contractDetail?.Title || "Untitled"}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexDirection: { xs: "column", sm: "row" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Chip
                icon={statusConfig.icon}
                label={contractDetail?.Status || "Status not available"}
                sx={{
                  backgroundColor: statusConfig.bg,
                  color: statusConfig.color,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: "center",
                }}
              />
              <Button
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={downloadPdf}
                disabled={downloadingPdf || loader}
                sx={{
                  borderColor: "primary.main",
                  color: "primary.light",
                  backgroundColor: (t) =>
                    t.palette.mode === "dark" ? "rgba(96, 165, 250, 0.12)" : t.palette.background.paper,
                  textTransform: "uppercase",
                  "&:hover": {
                    borderColor: "primary.light",
                    backgroundColor: (t) =>
                      t.palette.mode === "dark" ? "rgba(96, 165, 250, 0.2)" : "rgba(6, 49, 100, 0.04)",
                  },
                  "&:disabled": {
                    borderColor: "action.disabled",
                    color: "action.disabled",
                    backgroundColor: (t) =>
                      t.palette.mode === "dark"
                        ? t.palette.action.disabledBackground
                        : t.palette.background.paper,
                  },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {downloadingPdf ? "Loading..." : "Download PDF"}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 30%",
                lg: "1fr 25%",
              },
              width: "100%",
              maxWidth: "100%",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 3 },
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                flex: { xs: "1 1 100%", md: "1 1 auto" },
                minWidth: 0,
                maxWidth: { xs: "100%", md: "calc(100% - 380px)", lg: "calc(100% - 360px)" },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  mb: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "center", sm: "center" },
                    mb: 3,
                  }}
                >
                  <Avatar
                    sx={{
                      backgroundColor: "var(--bg-orange-color)",
                      width: { xs: 50, sm: 60 },
                      height: { xs: 50, sm: 60 },
                      mr: { xs: 0, sm: 3 },
                      fontSize: "1.5rem",
                      fontWeight: 600,
                    }}
                  >
                    {(() => {
                      const firstName = contractDetail?.customer?.FirstName || "";
                      const lastName = contractDetail?.customer?.LastName || "";
                      return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase() || "C";
                    })()}
                  </Avatar>
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: { xs: "center", sm: "left" },
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        mb: 1,
                        fontSize: { xs: "1.1rem", sm: "1.25rem" },
                      }}
                    >
                      {(() => {
                        const firstName = contractDetail?.customer?.FirstName || "First Name not available";
                        const lastName = contractDetail?.customer?.LastName || "Last Name not available";
                        return `${firstName} ${lastName}`.trim();
                      })()}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        flexWrap: "wrap",
                        gap: { xs: 1, sm: 3 },
                        justifyContent: { xs: "center", sm: "flex-start" },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LocationOnIcon
                          sx={{
                            fontSize: { xs: 16, sm: 18 },
                            color: "var(--text-orange-color)",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.primary",
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          }}
                        >
                          {formatAddress(contractDetail?.locationData)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PhoneIcon
                          sx={{
                            fontSize: { xs: 16, sm: 18 },
                            color: "var(--text-orange-color)",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          }}
                        >
                          {contractDetail?.customer?.PhoneNumber ||
                            "Phone Number not available"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      textAlign: { xs: "center", sm: "right" },
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Sent on
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      }}
                    >
                      {formatDateWithUserTimezone(contractDetail?.createdAt, {
                        dateFormat: DateDecode,
                        includeSeconds: false
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  mb: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <ReceiptIcon
                    sx={{
                      mr: 2,
                      color: "var(--text-orange-color)",
                      fontSize: { xs: 24, sm: 28 },
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "var(--text-blue-color)",
                      fontSize: { xs: "1.1rem", sm: "1.25rem" },
                    }}
                  >
                    Contract Items
                  </Typography>
                </Box>

                <Box sx={{ overflowX: "auto" }}>
                  <Box
                    sx={{
                      display: { xs: "none", md: "grid" },
                      gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr",
                      gap: 2,
                      p: 2,
                      backgroundColor: "action.hover",
                      borderRadius: 2,
                      mb: 2,
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: "0.9rem",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box>Materials/Labor</Box>
                    <Box>Image</Box>
                    <Box>Qty.</Box>
                    <Box>Unit Price</Box>
                    <Box>Total</Box>
                  </Box>

                  {contractDetail?.Items?.length > 0 ? (
                    contractDetail?.Items?.map((product, index) => (
                      <React.Fragment key={index}>
                        <Box
                          sx={{
                            display: { xs: "none", md: "grid" },
                            gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr",
                            gap: 2,
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            mb: 2,
                            backgroundColor: "background.paper",
                            "&:hover": {
                              backgroundColor: "action.hover",
                              borderColor: "primary.light",
                              transform: "translateY(-2px)",
                              transition: "all 0.3s ease",
                              boxShadow: (t) =>
                                t.palette.mode === "dark"
                                  ? "0 4px 12px rgba(0,0,0,0.35)"
                                  : "var(--shadow-md)",
                            },
                          }}
                        >
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                mb: 0.5,
                              }}
                            >
                              {product?.Name ||
                                "Enter Materials and Labor not available"}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary", fontSize: "0.85rem" }}
                            >
                              {product?.Description ||
                                "Description not available"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {product?.Attachment ? (
                              (() => {
                                const fileName = product.Attachment.toLowerCase();
                                const isImage = fileName.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);

                                if (isImage) {
                                  return (
                                    <img
                                      src={`${baseUrl}/v1/cdn/upload/${product.Attachment}`}
                                      alt={`Image for ${product.Name}`}
                                      style={{
                                        maxWidth: "100%",
                                        height: "auto",
                                        maxHeight: "60px",
                                        width: "100%",
                                        objectFit: "contain",
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: "6px",
                                        padding: "3px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        handleImageClick(
                                          `${baseUrl}/v1/cdn/upload/${product.Attachment}`
                                        )
                                      }
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "/assets/images/no-image.png";
                                      }}
                                      loading="lazy"
                                    />
                                  );
                                } else {
                                  return (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "text.secondary",
                                        fontStyle: "italic",
                                        fontSize: "0.85rem",
                                      }}
                                    >
                                      No image provided
                                    </Typography>
                                  );
                                }
                              })()
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontStyle: "italic",
                                  fontSize: "0.85rem",
                                }}
                              >
                                No image provided
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.primary",
                                fontSize: "0.9rem",
                              }}
                            >
                              {(() => {
                                let displayValue = "";
                                if (product?.Hourly) {
                                  displayValue = `Hourly: ${product?.Hourly}`;
                                } else if (product?.Unit) {
                                  displayValue = `Unit: ${product?.Unit}`;
                                } else if (product?.Square) {
                                  displayValue = `Sq. Ft.: ${product?.Square}`;
                                } else if (product?.Fixed) {
                                  displayValue = `Fixed: ${product?.Fixed}`;
                                }
                                return displayValue;
                              })()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: "0.9rem",
                              }}
                            >
                              $
                              {new Intl.NumberFormat("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(
                                product?.CostPerHour
                                  ? product.CostPerHour
                                  : product?.CostPerSquare
                                  ? product.CostPerSquare
                                  : product?.CostPerFixed
                                  ? product.CostPerFixed
                                  : product?.CostPerUnit || 0
                              )}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: "0.9rem",
                              }}
                            >
                              $
                              {new Intl.NumberFormat("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(Number(product?.Total) || 0)}
                            </Typography>
                          </Box>
                        </Box>

                        <Paper
                          elevation={0}
                          sx={{
                            display: { xs: "block", md: "none" },
                            p: { xs: 1.5, sm: 2 },
                            mb: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            backgroundColor: "background.paper",
                            boxShadow: "none",
                            "&:hover": {
                              backgroundColor: "action.hover",
                              borderColor: "primary.light",
                            },
                          }}
                        >
                          <Box sx={{ mb: 1 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: { xs: "1rem", sm: "1.1rem" },
                                mb: 0.5,
                              }}
                            >
                              {product?.Name ||
                                "Enter Materials and Labor not available"}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                                mb: 1,
                              }}
                            >
                              {product?.Description ||
                                "Description not available"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                            {product?.Attachment ? (
                              (() => {
                                const fileName = product.Attachment.toLowerCase();
                                const isImage = fileName.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);

                                if (isImage) {
                                  return (
                                    <img
                                      src={`${baseUrl}/v1/cdn/upload/${product.Attachment}`}
                                      alt={`Image for ${product.Name}`}
                                      style={{
                                        width: "100%",
                                        maxWidth: 60,
                                        height: "auto",
                                        maxHeight: 60,
                                        objectFit: "contain",
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 4,
                                        padding: 2,
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        handleImageClick(
                                          `${baseUrl}/v1/cdn/upload/${product.Attachment}`
                                        )
                                      }
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "/assets/images/no-image.png";
                                      }}
                                      loading="lazy"
                                    />
                                  );
                                } else {
                                  return (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "text.secondary",
                                        fontStyle: "italic",
                                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                                      }}
                                    >
                                      No image provided
                                    </Typography>
                                  );
                                }
                              })()
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontStyle: "italic",
                                  fontSize: { xs: "0.8rem", sm: "0.85rem" },
                                }}
                              >
                                No image provided
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", fontWeight: 600 }}
                            >
                              Quantity
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.primary",
                                fontWeight: 600,
                              }}
                            >
                              {(() => {
                                let displayValue = "";
                                if (product?.Hourly) {
                                  displayValue = `Hourly: ${product?.Hourly}`;
                                } else if (product?.Unit) {
                                  displayValue = `Unit: ${product?.Unit}`;
                                } else if (product?.Square) {
                                  displayValue = `Sq. Ft.: ${product?.Square}`;
                                } else if (product?.Fixed) {
                                  displayValue = `Fixed: ${product?.Fixed}`;
                                }
                                return displayValue;
                              })()}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", fontWeight: 600 }}
                            >
                              Unit Price
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                              }}
                            >
                              $
                              {new Intl.NumberFormat("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(
                                product?.CostPerHour
                                  ? product?.CostPerHour
                                  : product?.CostPerSquare
                                  ? product.CostPerSquare
                                  : product?.CostPerFixed
                                  ? product.CostPerFixed
                                  : product?.CostPerUnit || 0
                              )}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderTop: "1px solid",
                              borderColor: "divider",
                              pt: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", fontWeight: 600 }}
                            >
                              Total
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "text.primary",
                                fontSize: { xs: "1.1rem", sm: "1.2rem" },
                              }}
                            >
                              $
                              {new Intl.NumberFormat("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(Number(product?.Total) || 0)}
                            </Typography>
                          </Box>
                        </Paper>
                      </React.Fragment>
                    ))
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                      <Typography variant="body1">
                        No products available.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  mb: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <HelpOutlineOutlinedIcon
                    sx={{
                      mr: 2,
                      color: "var(--text-orange-color)",
                      fontSize: { xs: 20, sm: 24 },
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "var(--text-blue-color)",
                      fontSize: { xs: "1.1rem", sm: "1.25rem" },
                    }}
                  >
                    Internal Notes and Attachments
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "action.hover",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      lineHeight: 1.6,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    {contractDetail?.Notes || "No notes available"}
                  </Typography>
                </Box>
                {contractDetail?.Attachment && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: "var(--text-blue-color)",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      Attachments:
                    </Typography>
                    <DisplayImage
                      files={contractDetail?.Attachment}
                      IsDeleted={false}
                    />
                  </Box>
                )}
              </Paper>
            </Box>

            <Box
              sx={{
                order: { xs: 2, md: 2, lg: 0 },
                mb: { xs: 2, lg: 0 },
                width: { xs: "100%", md: "100%", lg: "auto" },
                flexShrink: 0,
                position: { xs: "static", md: "static", lg: "sticky" },
                top: { lg: 20 },
                zIndex: 1,
                alignSelf: { lg: "flex-start" },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  mb: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  width: { xs: "100%", md: "100%", lg: 360 },
                  minWidth: { xs: "100%", md: "100%", lg: 320 },
                  maxWidth: { xs: "100%", md: "100%", lg: 380 },
                  mx: { xs: 0, lg: 0 },
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: "success.main",
                    textAlign: "center",
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  }}
                >
                  Contract Summary
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      Sub Total
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      $
                      {new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(contractDetail?.subTotal || 0)}
                    </Typography>
                  </Box>
                  {contractDetail?.Discount && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        }}
                      >
                        Discount
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#27ae60",
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        }}
                      >
                        -${new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(((contractDetail?.subTotal || contractDetail?.SubTotal || 0) * (contractDetail?.Discount || 0)) / 100 || 0)} ({contractDetail?.Discount}%)
                      </Typography>
                    </Box>
                  )}
                  {contractDetail?.CalculatedTaxes?.length > 0 &&
                    contractDetail?.CalculatedTaxes?.map((item, index) => {
                      const isPercentageTax = item?.feeType === "Percentage" || (item?.percentage && item?.percentage > 0);
                      const taxAmount = item?.amount ?? 0;
                      const formattedAmount = new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(taxAmount);
                      
                      let taxLabel;
                      if (isPercentageTax && item?.percentage > 0) {
                        taxLabel = `${item?.name} (${item?.percentage}%) ($${formattedAmount})${item?.type ? ` - ${item?.type}` : ""}`;
                      } else {
                        taxLabel = `${item?.name} ($${formattedAmount})${item?.type ? ` - ${item?.type}` : ""}`;
                      }
                      
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                            }}
                          >
                            {taxLabel}
                          </Typography>
                        </Box>
                      );
                    })}
                  {contractDetail?.Tax && Number(contractDetail?.Tax) > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        }}
                      >
                        Tax
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#e74c3c",
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        }}
                      >
                        {(() => {
                          const subTotal = Number(contractDetail?.subTotal || contractDetail?.SubTotal || 0);
                          const discount = Number(contractDetail?.Discount || 0);
                          const tax = Number(contractDetail?.Tax || 0);
                          
                          // Calculate discount amount
                          const discountAmount = discount > 0 ? (subTotal * discount) / 100 : 0;
                          const discountedTotal = Math.max(0, subTotal - discountAmount);
                          
                          // Calculate tax on discounted total (same as frontend logic)
                          // Use proper rounding to match frontend calculation exactly
                          const taxAmount = tax > 0 ? Math.round((discountedTotal * tax)) / 100 : 0;
                          
                          return `+$${new Intl.NumberFormat("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(taxAmount)} (${tax}%)`;
                        })()}
                      </Typography>
                    </Box>
                  )}
                  {(() => {
                    const hasCalculatedTaxes = (contractDetail?.CalculatedTaxes || []).length > 0;
                    const hasLegacyTax = Number(contractDetail?.Tax || 0) > 0;
                    if (!hasCalculatedTaxes && !hasLegacyTax) return null;
                    const calculatedTaxTotal = (contractDetail?.CalculatedTaxes || []).reduce((sum, t) => sum + parseFloat(t?.amount || 0), 0);
                    const subTotal = parseFloat(contractDetail?.subTotal || contractDetail?.SubTotal || 0);
                    const discount = parseFloat(contractDetail?.Discount || 0);
                    const discountAmount = discount > 0 ? (subTotal * discount) / 100 : 0;
                    const discountedTotal = Math.max(0, subTotal - discountAmount);
                    const legacyTaxAmount = hasLegacyTax ? Math.round((discountedTotal * Number(contractDetail?.Tax))) / 100 : 0;
                    const taxSubtotal = calculatedTaxTotal + legacyTaxAmount;
                    return (
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                          Tax Subtotal
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                          ${new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(taxSubtotal)}
                        </Typography>
                      </Box>
                    );
                  })()}
                  <Divider sx={{ my: 2 }} />
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      }}
                    >
                      Total
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: (t) =>
                          t.palette.mode === "dark" ? t.palette.success.light : t.palette.success.main,
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                      }}
                    >
                      $
                      {new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(contractDetail?.Total || 0)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      )}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        maxWidth={previewContent?.type === "csv" || previewContent?.type === "excel" ? "lg" : modalWidth}
        fullWidth={previewContent?.type === "csv" || previewContent?.type === "excel"}
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: "background.paper",
            boxShadow: (t) =>
              t.palette.mode === "dark" ? "0 8px 32px rgba(0,0,0,0.45)" : "var(--shadow-xl)",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            width: "auto",
            maxWidth: previewContent?.type === "csv" || previewContent?.type === "excel" ? "1200px" : (modalWidth === "xs" ? "400px" : modalWidth === "sm" ? "500px" : modalWidth === "md" ? "700px" : "900px"),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                color: "text.primary",
              }}
            >
              Preview
            </Typography>
            {(previewContent?.type === "csv" || previewContent?.type === "excel") && (
              <Chip
                label={previewContent?.type === "csv" ? "CSV" : "EXCEL"}
                style={{
                  backgroundColor: previewContent?.type === "csv" ? "#0f2b46" : "#217346",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "12px",
                  height: "24px",
                }}
              />
            )}
          </Box>
          {fileType === "image" && (
            <Box
              sx={{
                display: "flex",
                gap: "4px",
                alignItems: "center",
                backgroundColor: "action.hover",
                padding: "4px 8px",
                borderRadius: "8px",
              }}
            >
              <IconButton
                onClick={handleZoomOut}
                disabled={imageZoom <= 50}
                size="small"
                sx={{
                  color: imageZoom <= 50 ? "action.disabled" : "text.primary",
                  padding: "4px",
                }}
                title="Zoom Out"
              >
                <ZoomOut fontSize="small" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  minWidth: "50px",
                  textAlign: "center",
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: "14px",
                  padding: "0 8px",
                }}
              >
                {imageZoom}%
              </Typography>
              <IconButton
                onClick={handleZoomIn}
                disabled={imageZoom >= 300}
                size="small"
                sx={{
                  color: imageZoom >= 300 ? "action.disabled" : "text.primary",
                  padding: "4px",
                }}
                title="Zoom In"
              >
                <ZoomIn fontSize="small" />
              </IconButton>
              {imageZoom !== 100 && (
                <Button
                  onClick={handleResetZoom}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: "text.primary",
                    borderColor: "divider",
                    marginLeft: "8px",
                    padding: "2px 12px",
                    fontSize: "12px",
                    textTransform: "none",
                    minWidth: "auto",
                  }}
                  title="Reset Zoom"
                >
                  Reset
                </Button>
              )}
              <IconButton
                onClick={handleDownload}
                size="small"
                sx={{ 
                  color: "text.primary",
                  marginLeft: "8px",
                  padding: "4px",
                }}
                title="Download"
              >
                <FileDownloadOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleClosePreview}
                size="small"
                sx={{
                  color: "text.primary",
                  marginLeft: "8px",
                  padding: "4px",
                }}
                title="Close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          {(fileType === "pdf" || fileType === "txt" || fileType === "csv" || fileType === "excel") && (
            <Box
              sx={{
                display: "flex",
                gap: "4px",
                alignItems: "center",
              }}
            >
              <IconButton
                onClick={handleDownload}
                size="small"
                sx={{ 
                  color: "text.primary",
                  padding: "4px",
                }}
                title="Download"
              >
                <FileDownloadOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleClosePreview}
                size="small"
                sx={{
                  color: "text.primary",
                  padding: "4px",
                }}
                title="Close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </DialogTitle>
        <DialogContent
          sx={{
            textAlign: "center",
            padding: "0",
            overflow: "hidden",
            backgroundColor: "#f5f5f5",
            position: "relative",
            flex: "1 1 auto",
            minHeight: 0,
            maxHeight: "calc(90vh - 120px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {isLoadingPreview ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              minHeight={400}
            >
              <CircularProgress sx={{ color: "#D4943A" }} />
            </Box>
          ) : (previewContent?.type === "csv" || previewContent?.type === "excel") ? (
            previewContent?.error ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "40vh",
                  padding: "40px",
                }}
              >
                <DescriptionIcon
                  sx={{
                    width: "80px",
                    height: "80px",
                    color: previewContent?.type === "csv" ? "#0f2b46" : "#217346",
                    marginBottom: "20px",
                  }}
                />
                <Typography variant="h6" sx={{ marginBottom: "10px", fontWeight: 600 }}>
                  {previewImage.split("/").pop() || `${previewContent?.type === "csv" ? "CSV" : "Excel"} File`}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", marginBottom: "10px" }}>
                  Failed to preview {previewContent?.type === "csv" ? "CSV" : "Excel"} file: {previewContent.error}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
                  Please download the file to view its contents.
                </Typography>
              </Box>
            ) : previewContent?.data && previewContent.data.length > 0 ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: "calc(90vh - 220px)",
                    overflow: "auto",
                    borderRadius: 0,
                    "& .MuiTable-root": {
                      borderCollapse: "separate",
                      borderSpacing: 0,
                    },
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {previewContent?.data?.[0]?.map((header, index) => (
                          <TableCell
                            key={index}
                            className="bg-blue-color"
                            sx={{
                              textAlign: "left",
                              color: "white",
                              fontWeight: "bold",
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                              padding: { xs: "8px 12px", sm: "12px 16px" },
                              backgroundColor: "#2c3e50",
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              "&:not(:last-child)": {
                                borderRight: "1px solid rgba(224, 224, 224, 0.4)",
                              },
                            }}
                            title={header}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getPreviewData().map((row, rowIndex) => (
                        <TableRow
                          key={rowIndex}
                          sx={{
                            "&:nth-of-type(even)": {
                              backgroundColor: "action.hover",
                            },
                            "&:hover": {
                              backgroundColor: "action.selected",
                            },
                          }}
                        >
                          {row.map((cell, cellIndex) => (
                            <TableCell
                              key={cellIndex}
                              sx={{
                                padding: { xs: "8px 12px", sm: "12px 16px" },
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                borderRight: "1px solid",
                                borderColor: "divider",
                                color: "text.primary",
                              }}
                            >
                              {cell || ""}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={getTotalRows()}
                  page={previewPage}
                  onPageChange={handlePreviewPageChange}
                  rowsPerPage={previewRowsPerPage}
                  onRowsPerPageChange={handlePreviewRowsPerPageChange}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    color: "text.primary",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "40vh",
                  padding: "40px",
                }}
              >
                <DescriptionIcon
                  sx={{
                    width: "80px",
                    height: "80px",
                    color: previewContent?.type === "csv" ? "#0f2b46" : "#217346",
                    marginBottom: "20px",
                  }}
                />
                <Typography variant="h6" sx={{ marginBottom: "10px", fontWeight: 600 }}>
                  {previewImage.split("/").pop() || `${previewContent?.type === "csv" ? "CSV" : "Excel"} File`}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
                  {previewContent?.type === "csv" ? "CSV" : "Excel"} file is empty or has no data to display.
                </Typography>
              </Box>
            )
          ) : fileType === "pdf" ? (
            <iframe
              src={previewImage}
              style={{
                width: "100%",
                height: "50vh",
                border: "none",
                borderRadius: "8px",
              }}
              title="PDF Preview"
            />
          ) : fileType === "txt" ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "40vh",
                padding: "40px",
              }}
            >
              <InsertDriveFileIcon
                sx={{
                  width: "80px",
                  height: "80px",
                  color: "text.primary",
                  marginBottom: "20px",
                }}
              />
              <Typography variant="h6" sx={{ marginBottom: "10px", fontWeight: 600 }}>
                {previewImage.split("/").pop() || "Text File"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
                Text files cannot be previewed in the browser. Please download to view.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "40vh",
                padding: "20px",
                width: "100%",
                boxSizing: "border-box",
                overflow: "auto",
              }}
            >
              <img
                ref={imgRef}
                src={previewImage}
                alt="Preview"
                onLoad={updateImageSize}
                style={{
                  width: "auto",
                  maxWidth: imageZoom === 100 ? "100%" : "none",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "var(--shadow-md)",
                  objectFit: "contain",
                  transition: "width 0.3s ease",
                  display: "block",
                }}
                draggable={false}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/assets/images/no-image.png";
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "8px 24px" }}>
          <WhiteButton
            onClick={handleClosePreview}
            label="Close"
          />
        </DialogActions>
      </Dialog>
      <Dialog
        open={openPdfPreview}
        onClose={handleClosePdfPreview}
        maxWidth="lg"
        fullWidth
        className="p-0"
        style={{ background: "transparent" }}
        PaperProps={{
          sx: {
            maxHeight: "95vh",
            height: "95vh",
          },
        }}
      >
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
          {previewPdfUrl && (
            <iframe
              src={previewPdfUrl}
              width="100%"
              height="100%"
              title="PDF Preview"
              style={{ minHeight: "calc(100vh - 160px)", border: "none", flex: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "10px", gap: 1 }}>
          <BlueButton onClick={handleClosePdfPreview} label="Close" />
          <BlueButton onClick={handleDownloadFromPreview} label="Download" />
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ContractsDetails;
